/**
 * Travel Assistant Data Transformer
 *
 * Single Responsibility: Transforms API responses to domain models
 * Open/Closed Principle: Can be extended without modifying existing code
 */

import {
  OverviewResponse,
  Festival,
  PlaceOfInterest,
  LocationOverview,
  QuickPeekResponse,
  TravelMode,
  Accessibility,
  TerrainType,
  LocationHistoryItem,
  FoodSpecialty,
  LocalExperience,
  HandicraftSouvenir,
  Activities,
  AmenitiesAndFacilities,
  SafetyAndSecurity,
  CulturalAspects,
  HistoricalSignificance,
} from '../interfaces/ITravelAssistantRepository';

export interface ApiOverviewResponse {
  code?: number;
  success?: boolean;
  message?: string;
  data?: {
    response?: {
      festivals?: Array<{
        title: string;
        description: string;
        longDescription: string;
        imageUrls: string[];
        images?: Array<{
          url: string;
          credits: string;
        }>;
      }>;
      placesOfInterest?: Array<{
        title: string;
        description: string;
        longDescription: string;
        imageUrls: string[];
        images?: Array<{
          url: string;
          credits: string;
        }>;
      }>;
      overview?: {
        name: string;
        type: string;
        description: string;
        district: string;
        city: string;
        state: string;
        pincode: string;
        rating: string;
        reviews: string;
        imageUrls: string[];
        images?: Array<{
          url: string;
          credits: string;
        }>;
        pointers?: Array<{
          title: string;
          items: Array<{
            title: string;
            value: string | number;
            icon?: string;
            tag?: string;
            subtitle?: string;
          }>;
        }>;
      };
      travelDetails?: Record<string, unknown>;
    };
  };
  timeStamp?: string;
  execTime?: {
    inMs: number;
    inSec: number;
  };
  [key: string]: unknown;
}

export class TravelAssistantTransformer {
  /**
   * Labels that are amenities/practical info, not activities or attractions.
   * Exclude these from "Activities & Attractions" so only real attractions show.
   */
  private static readonly NON_ATTRACTION_LABELS = [
    'have money available',
    'money available',
    'money',
    'atm',
    'atms',
    'currency',
    'budget',
    'salary',
    'income',
    'employment',
    'recruitment',
    'consultation',
    'attractions nearby',
    'nearby attractions',
    'paid activities',
    'activities nearby',
  ] as const;

  private static isNonAttractionLabel(label: string): boolean {
    if (!label || typeof label !== 'string') return true;
    const lower = label.toLowerCase().trim();
    return this.NON_ATTRACTION_LABELS.some(
      bad => lower === bad || lower.includes(bad)
    );
  }

  /**
   * Helper function to check if a string is placeholder/unknown
   */
  private static isPlaceholder(value: unknown): boolean {
    // Early return for null/undefined
    if (value === null || value === undefined) {
      return true;
    }

    // If it's already a string, use it directly
    if (typeof value === 'string') {
      try {
        const lowerValue = value.toLowerCase().trim();
        return (
          lowerValue.includes('placeholder') ||
          lowerValue === 'unknown' ||
          lowerValue === 'not known' ||
          lowerValue === 'n/a' ||
          lowerValue === ''
        );
      } catch {
        // If toLowerCase fails on a string (shouldn't happen), treat as placeholder
        return true;
      }
    }

    // For all other types, convert to string first
    let stringValue: string;
    try {
      // Use String() constructor which always returns a string primitive
      stringValue = String(value);

      // Verify we got a string
      if (typeof stringValue !== 'string') {
        return true; // Treat as placeholder if conversion failed
      }

      // Now safely call toLowerCase
      const lowerValue = stringValue.toLowerCase().trim();
      return (
        lowerValue.includes('placeholder') ||
        lowerValue === 'unknown' ||
        lowerValue === 'not known' ||
        lowerValue === 'n/a' ||
        lowerValue === ''
      );
    } catch {
      // If any error occurs during conversion or processing, treat as placeholder
      return true;
    }
  }

  /**
   * Get image URLs from item (imageUrls array, images array, or image string). Used for sections that support images.
   */
  private static getImageUrls(
    item:
      | {
          image?: string;
          imageUrl?: string;
          imageUrls?: string[] | null;
          images?: Array<{ url?: string }> | null;
        }
      | null
      | undefined
  ): string[] {
    if (!item || typeof item !== 'object') return [];
    if (typeof item.image === 'string' && item.image.trim())
      return [item.image.trim()];
    if (typeof (item as { imageUrl?: string }).imageUrl === 'string') {
      const u = (item as { imageUrl: string }).imageUrl.trim();
      if (u) return [u];
    }
    if (Array.isArray(item.imageUrls) && item.imageUrls.length > 0)
      return item.imageUrls.filter(
        (u): u is string => typeof u === 'string' && !!u.trim()
      );
    if (Array.isArray(item.images) && item.images.length > 0)
      return item.images
        .map(img => (typeof img?.url === 'string' ? img.url.trim() : null))
        .filter((u): u is string => !!u);
    return [];
  }

  /**
   * Transform API response to OverviewResponse
   */
  static transformOverview(apiResponse: ApiOverviewResponse): OverviewResponse {
    // Extract response data from nested structure
    const responseData = apiResponse?.data?.response;

    if (!responseData) {
      // Return empty structure if no data
      return {
        festivals: [],
        placesOfInterest: [],
        overview: {
          name: '',
          type: '',
          description: '',
          district: '',
          city: '',
          state: '',
          pincode: '',
          rating: '',
          reviews: '',
          imageUrls: [],
          pointers: [],
        },
        travelDetails: {},
      };
    }

    // Helper: get image URLs from either imageUrls (string[]) or images ({ url }[]). Safe when either is missing.
    const getImageUrls = (
      item:
        | {
            imageUrls?: string[] | null;
            images?: Array<{ url?: string }> | null;
          }
        | null
        | undefined
    ): string[] => {
      if (!item || typeof item !== 'object') return [];
      if (Array.isArray(item.imageUrls) && item.imageUrls.length > 0)
        return item.imageUrls;
      if (Array.isArray(item.images) && item.images.length > 0)
        return item.images
          .map(img => (typeof img?.url === 'string' ? img.url : null))
          .filter((u): u is string => !!u);
      return [];
    };

    // Transform festivals - filter out placeholders; ensure imageUrls from images when needed
    const festivals: Festival[] =
      responseData.festivals
        ?.filter(
          festival => festival.title && !this.isPlaceholder(festival.title)
        )
        .map(festival => ({
          title: festival.title || '',
          description: festival.description || '',
          longDescription: festival.longDescription || '',
          imageUrls: getImageUrls(festival),
          images: festival.images || [],
        })) || [];

    // Transform places of interest - filter out placeholders; ensure imageUrls from images when needed
    const placesOfInterest: PlaceOfInterest[] =
      responseData.placesOfInterest
        ?.filter(place => place.title && !this.isPlaceholder(place.title))
        .map(place => ({
          title: place.title || '',
          description: place.description || '',
          longDescription: place.longDescription || '',
          imageUrls: getImageUrls(place),
          images: place.images || [],
        })) || [];

    // Transform overview - filter out placeholder/unknown data
    const overviewData = responseData.overview;

    // Check if overview has valid data - even if name is "Unknown Location",
    // we should still show data if there's description, pointers, or images
    const hasValidName =
      overviewData?.name &&
      typeof overviewData.name === 'string' &&
      !this.isPlaceholder(overviewData.name) &&
      overviewData.name !== 'Unknown Location';
    const hasValidDescription =
      overviewData?.description &&
      typeof overviewData.description === 'string' &&
      overviewData.description.trim() !== '' &&
      !this.isPlaceholder(overviewData.description);
    const hasValidImages =
      Array.isArray(overviewData?.imageUrls) &&
      overviewData.imageUrls.length > 0;

    // Overview is valid if it has any meaningful data
    // Note: isValidOverview is checked implicitly through hasValidName, hasValidDescription, etc.

    // Process pointers - keep all items, even with placeholder values
    // The UI components will handle displaying "No Data Available" for invalid values
    const processedPointers =
      overviewData?.pointers
        ?.map(pointer => ({
          title: pointer.title || '',
          items:
            pointer.items?.map(item => {
              // Convert value to string if it's not already (API may return numbers)
              const rawValue = item.value;
              let stringValue: string | null = null;

              if (rawValue != null) {
                if (typeof rawValue === 'string') {
                  stringValue = rawValue;
                } else if (typeof rawValue === 'number') {
                  stringValue = String(rawValue);
                } else {
                  // For other types (objects, arrays, etc.), convert safely
                  try {
                    stringValue = String(rawValue);
                  } catch {
                    stringValue = null;
                  }
                }
              }

              // Normalize placeholder values to null for UI components to handle
              const isPlaceholderResult = stringValue
                ? this.isPlaceholder(stringValue)
                : true;
              const value =
                stringValue &&
                typeof stringValue === 'string' &&
                stringValue !== 'Not known' &&
                stringValue !== 'N/A' &&
                stringValue !== '0' &&
                !isPlaceholderResult
                  ? stringValue
                  : null;

              return {
                title: item.title || '',
                value: value,
                icon: item.icon,
                tag: item.tag,
                subtitle: item.subtitle,
              };
            }) || [],
        }))
        .filter(pointer => pointer.items.length > 0) || [];

    const overview: LocationOverview = {
      name: hasValidName ? overviewData?.name || '' : overviewData?.name || '',
      type: overviewData?.type || '',
      description: hasValidDescription
        ? overviewData?.description || ''
        : overviewData?.description || '',
      district: overviewData?.district || '',
      city: overviewData?.city || '',
      state: overviewData?.state || '',
      pincode: overviewData?.pincode || '',
      rating: overviewData?.rating || '',
      reviews: overviewData?.reviews || '',
      imageUrls: hasValidImages ? overviewData?.imageUrls || [] : [],
      images: hasValidImages ? overviewData?.images || [] : [],
      pointers: processedPointers,
    };

    return {
      festivals,
      placesOfInterest,
      overview,
      travelDetails: responseData.travelDetails || {},
    };
  }

  /**
   * Transform API response to QuickPeekResponse
   */
  static transformQuickPeek(apiResponse: {
    code?: number;
    success?: boolean;
    message?: string | null;
    data?: {
      response?: {
        bestTimeToVisit?: string;
        travelModes?: Array<{
          id?: string;
          label?: string;
          icon?: string;
          recommended?: boolean;
          fare?: string;
          description?: string;
        }>;
        travelModeDescription?: string;
        accessibility?: {
          title?: string;
          items?: Array<{
            id?: string;
            heading?: string;
            description?: string;
            expanded?: boolean;
          }>;
        };
        terrainTypes?: Array<{
          id?: string;
          title?: string;
          image?: string;
          description?: string;
        }>;
        navigationDifficulty?:
          | string
          | {
              title?: string;
              items?: string[];
            }
          | null;
        locationHistory?: Array<{
          id?: string;
          title?: string;
          image?: string;
          description?: string;
        }>;
        historicalSignificance?:
          | {
              title?: string;
              items?: string[];
            }
          | Array<{
              id?: string;
              title?: string;
              description?: string;
            }>
          | null;
        culturalAspects?:
          | {
              title?: string;
              items?: string[];
            }
          | Array<{
              id?: string;
              title?: string;
              description?: string;
            }>
          | null;
        culturalAndSocialAspects?:
          | {
              title?: string;
              items?: string[];
            }
          | Array<{
              id?: string;
              title?: string;
              description?: string;
            }>
          | null;
        foodSpecialties?: Array<{
          id?: string;
          name?: string;
          image?: string;
          description?: string;
        }>;
        localLanguages?: Array<{
          id?: string;
          name?: string;
          description?: string;
        }>;
        localExperiences?: Array<{
          id?: string;
          title?: string;
          image?: string;
          description?: string;
          price?: string;
        }>;
        handicraftsAndSouvenirs?: Array<{
          id?: string;
          title?: string;
          image?: string;
          description?: string;
        }>;
        activities?: {
          title?: string;
          items?: Array<{
            icon?: string;
            label?: string;
          }>;
        };
        amenitiesAndFacilities?: {
          title?: string;
          items?: Array<{
            id?: string;
            label?: string;
            icon?: string;
          }>;
        };
        safetyAndSecurity?: {
          title?: string;
          sections?: Array<{
            id?: string;
            title?: string;
            items?: string[];
          }>;
        };
        [key: string]: unknown;
      };
      [key: string]: unknown;
    };
    response?: {
      bestTimeToVisit?: string;
      travelModes?: Array<{
        id?: string;
        label?: string;
        icon?: string;
        recommended?: boolean;
        fare?: string;
        description?: string;
      }>;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  }): QuickPeekResponse {
    // Debug logging to understand the response structure
    console.log(
      '🔍 [TravelAssistantTransformer] transformQuickPeek received:',
      {
        hasApiResponse: !!apiResponse,
        apiResponseKeys: apiResponse ? Object.keys(apiResponse) : [],
        hasData: !!(apiResponse as { data?: unknown })?.data,
        hasResponse: !!(apiResponse as { response?: unknown })?.response,
        hasDataResponse: !!(apiResponse as { data?: { response?: unknown } })
          ?.data?.response,
        apiResponseStructure: JSON.stringify(apiResponse, null, 2).substring(
          0,
          500
        ),
      }
    );

    // Extract response data from nested structure
    // Handle multiple possible response structures:
    // 1. { data: { response: {...} } } - Standard API response
    // 2. { response: {...} } - Direct response
    // 3. { code: 200, success: true, data: { response: {...} } } - Wrapped API response
    const responseData = ((apiResponse as { data?: { response?: unknown } })
      ?.data?.response ||
      (apiResponse as { response?: unknown })?.response ||
      // Handle case where apiResponse itself is the response object
      (apiResponse as {
        bestTimeToVisit?: unknown;
        travelModes?: unknown;
      })) as {
      bestTimeToVisit?: string;
      travelModes?: Array<{
        id?: string;
        label?: string;
        icon?: string;
        recommended?: boolean;
        fare?: string;
        description?: string;
      }>;
      travelModeDescription?: string;
      accessibility?: {
        title?: string;
        items?: Array<{
          id?: string;
          heading?: string;
          description?: string;
          expanded?: boolean;
        }>;
      };
      terrainTypes?: Array<{
        id?: string;
        title?: string;
        image?: string;
        description?: string;
      }>;
      navigationDifficulty?:
        | string
        | {
            title?: string;
            items?: string[];
          }
        | null;
      locationHistory?: Array<{
        id?: string;
        title?: string;
        image?: string;
        description?: string;
      }>;
      historicalSignificance?: {
        title?: string;
        items?: string[];
      } | null;
      culturalAspects?: {
        title?: string;
        items?: string[];
      } | null;
      foodSpecialties?: Array<{
        id?: string;
        name?: string;
        image?: string;
        description?: string;
      }>;
      localExperiences?: Array<{
        id?: string;
        title?: string;
        image?: string;
        description?: string;
        price?: string;
      }>;
      handicraftsAndSouvenirs?: Array<{
        id?: string;
        title?: string;
        image?: string;
        description?: string;
      }>;
      activities?: {
        title?: string;
        items?: Array<{
          icon?: string;
          label?: string;
        }>;
      };
      amenitiesAndFacilities?: {
        title?: string;
        items?: Array<{
          id?: string;
          label?: string;
          icon?: string;
        }>;
      };
      safetyAndSecurity?: {
        title?: string;
        sections?: Array<{
          id?: string;
          title?: string;
          items?: string[];
        }>;
      };
      [key: string]: unknown;
    };

    if (!responseData) {
      // Return empty structure if no data
      console.warn('⚠️ [TravelAssistantTransformer] No responseData found:', {
        apiResponse,
        hasData: !!(apiResponse as { data?: unknown })?.data,
        hasResponse: !!(apiResponse as { response?: unknown })?.response,
        hasDataResponse: !!(apiResponse as { data?: { response?: unknown } })
          ?.data?.response,
      });
      return {};
    }

    console.log('✅ [TravelAssistantTransformer] responseData found:', {
      hasBestTimeToVisit: !!responseData.bestTimeToVisit,
      travelModesCount: responseData.travelModes?.length || 0,
      hasAccessibility: !!responseData.accessibility,
      terrainTypesCount: responseData.terrainTypes?.length || 0,
      responseDataKeys: Object.keys(responseData),
    });

    // Transform best time to visit
    const bestTimeToVisit = responseData.bestTimeToVisit
      ? responseData.bestTimeToVisit.trim()
      : undefined;

    // Transform travel modes
    const travelModes: TravelMode[] =
      responseData.travelModes
        ?.filter(
          mode =>
            mode.id &&
            mode.label &&
            mode.description &&
            !this.isPlaceholder(mode.label) &&
            !this.isPlaceholder(mode.description)
        )
        .map(mode => ({
          id: mode.id || '',
          label: mode.label || '',
          icon: mode.icon || '',
          recommended: mode.recommended,
          fare: mode.fare,
          description: mode.description || '',
        })) || [];

    // Transform accessibility
    const accessibility: Accessibility | undefined =
      responseData.accessibility &&
      typeof responseData.accessibility === 'object' &&
      'items' in responseData.accessibility
        ? {
            title:
              (responseData.accessibility.title as string) || 'Who can Travel?',
            items: Array.isArray(responseData.accessibility.items)
              ? responseData.accessibility.items
                  .filter(
                    (item: {
                      id?: string;
                      heading?: string;
                      description?: string;
                      expanded?: boolean;
                    }) =>
                      item.heading &&
                      item.description &&
                      !this.isPlaceholder(item.heading) &&
                      !this.isPlaceholder(item.description)
                  )
                  .map(item => ({
                    id: item.id || '',
                    heading: item.heading || '',
                    description: item.description || '',
                    expanded: item.expanded,
                  }))
              : [],
          }
        : undefined;

    // Transform terrain types
    const terrainTypes: TerrainType[] =
      responseData.terrainTypes
        ?.filter(
          terrain =>
            terrain.id &&
            terrain.title &&
            terrain.description &&
            !this.isPlaceholder(terrain.title) &&
            !this.isPlaceholder(terrain.description)
        )
        .map(terrain => ({
          id: terrain.id || '',
          title: terrain.title || '',
          image: terrain.image,
          description: terrain.description || '',
        })) || [];

    // Transform location history
    const locationHistory: LocationHistoryItem[] =
      responseData.locationHistory
        ?.filter(
          history =>
            history.id &&
            history.title &&
            history.description &&
            !this.isPlaceholder(history.title) &&
            !this.isPlaceholder(history.description)
        )
        .map(history => ({
          id: history.id || '',
          title: history.title || '',
          image: history.image,
          description: history.description || '',
        })) || [];

    // Transform food specialties
    const foodSpecialties: FoodSpecialty[] =
      responseData.foodSpecialties
        ?.filter(
          food =>
            food.id &&
            food.name &&
            food.description &&
            !this.isPlaceholder(food.name) &&
            !this.isPlaceholder(food.description)
        )
        .map(food => {
          // Extract image URL - handle both string and object formats
          let imageUrl: string | undefined = undefined;
          if (food.image) {
            if (typeof food.image === 'string') {
              imageUrl = food.image;
            } else if (
              typeof food.image === 'object' &&
              food.image !== null &&
              'url' in food.image
            ) {
              imageUrl = (food.image as { url: string }).url;
            }
          }
          return {
            id: food.id || '',
            name: food.name || '',
            image: imageUrl,
            description: food.description || '',
          };
        }) || [];

    // Transform local languages
    const localLanguages =
      responseData.localLanguages && Array.isArray(responseData.localLanguages)
        ? responseData.localLanguages
            .filter(
              lang =>
                lang &&
                lang.name &&
                lang.description &&
                !this.isPlaceholder(lang.name) &&
                !this.isPlaceholder(lang.description)
            )
            .map((lang, index) => ({
              id: lang.id || `lang-${index}`,
              name: lang.name || '',
              description: lang.description || '',
            }))
        : [];

    // Transform local experiences
    const localExperiences: LocalExperience[] =
      responseData.localExperiences
        ?.filter(
          exp =>
            exp.id &&
            exp.title &&
            exp.description &&
            !this.isPlaceholder(exp.title) &&
            !this.isPlaceholder(exp.description)
        )
        .map(exp => ({
          id: exp.id || '',
          title: exp.title || '',
          image: exp.image,
          description: exp.description || '',
          price: exp.price,
        })) || [];

    // Transform handicrafts and souvenirs
    const handicraftsAndSouvenirsItems =
      responseData.handicraftsAndSouvenirs
        ?.filter(
          item =>
            item.id &&
            item.title &&
            item.description &&
            !this.isPlaceholder(item.title) &&
            !this.isPlaceholder(item.description)
        )
        .map(item => {
          // Extract image URL - handle both string and object formats
          let imageUrl: string | undefined = undefined;
          if (item.image) {
            if (typeof item.image === 'string') {
              imageUrl = item.image;
            } else if (
              typeof item.image === 'object' &&
              item.image !== null &&
              'url' in item.image
            ) {
              imageUrl = (item.image as { url: string }).url;
            }
          }
          return {
            id: item.id || '',
            title: item.title || '',
            image: imageUrl,
            description: item.description || '',
          };
        }) || [];

    const handicraftsAndSouvenirs: HandicraftSouvenir[] | undefined =
      handicraftsAndSouvenirsItems.length > 0
        ? handicraftsAndSouvenirsItems
        : undefined;

    // Debug logging
    if (responseData.handicraftsAndSouvenirs) {
      console.log('🔍 [Transformer] handicraftsAndSouvenirs found:', {
        raw: responseData.handicraftsAndSouvenirs,
        filtered: handicraftsAndSouvenirsItems,
        final: handicraftsAndSouvenirs,
      });
    }

    // Transform activities
    const activities: Activities | undefined =
      responseData.activities &&
      typeof responseData.activities === 'object' &&
      'items' in responseData.activities
        ? {
            title:
              (responseData.activities.title as string) ||
              'Activities and Attractions',
            items: Array.isArray(responseData.activities.items)
              ? responseData.activities.items
                  .filter(
                    (item: { icon?: string | null; label?: string }) =>
                      item.label &&
                      !this.isPlaceholder(item.label) &&
                      !this.isNonAttractionLabel(item.label)
                  )
                  .map(
                    (item: {
                      id?: string;
                      icon?: string | null;
                      label?: string;
                    }) => ({
                      id: item.id || undefined,
                      icon: item.icon || '',
                      label: item.label || '',
                    })
                  )
              : [],
          }
        : undefined;

    // Transform amenities and facilities
    const amenitiesAndFacilitiesItems = Array.isArray(
      responseData.amenitiesAndFacilities?.items
    )
      ? responseData.amenitiesAndFacilities.items
          .filter(
            (item: { id?: string; label?: string; icon?: string | null }) =>
              item.id && item.label && !this.isPlaceholder(item.label)
          )
          .map(item => ({
            id: item.id || '',
            label: item.label || '',
            icon: item.icon || '',
          }))
      : [];

    const amenitiesAndFacilities: AmenitiesAndFacilities | undefined =
      responseData.amenitiesAndFacilities &&
      typeof responseData.amenitiesAndFacilities === 'object' &&
      'items' in responseData.amenitiesAndFacilities &&
      amenitiesAndFacilitiesItems.length > 0
        ? {
            title:
              (responseData.amenitiesAndFacilities.title as string) ||
              'Amenities & Facilities',
            items: amenitiesAndFacilitiesItems,
          }
        : undefined;

    // Transform safety and security
    const safetyAndSecurity: SafetyAndSecurity | undefined =
      responseData.safetyAndSecurity &&
      typeof responseData.safetyAndSecurity === 'object' &&
      'sections' in responseData.safetyAndSecurity
        ? {
            title:
              (responseData.safetyAndSecurity.title as string) ||
              'Safety & Security',
            sections: Array.isArray(responseData.safetyAndSecurity.sections)
              ? responseData.safetyAndSecurity.sections
                  .filter(
                    (section: {
                      id?: string;
                      title?: string;
                      items?: string[];
                    }) =>
                      section.id &&
                      section.title &&
                      section.items &&
                      section.items.length > 0 &&
                      !this.isPlaceholder(section.title)
                  )
                  .map(section => ({
                    id: section.id || '',
                    title: section.title || '',
                    items: section.items || [],
                  }))
              : [],
          }
        : undefined;

    // Transform cultural aspects
    // Handle both culturalAspects and culturalAndSocialAspects (API field name)
    const culturalAspectsData =
      responseData.culturalAndSocialAspects || responseData.culturalAspects;

    const culturalAspects: CulturalAspects | undefined = (() => {
      if (!culturalAspectsData) return undefined;

      // Handle array format (new API format): [{id, title, description}, ...]
      if (
        Array.isArray(culturalAspectsData) &&
        culturalAspectsData.length > 0
      ) {
        const validItems = culturalAspectsData
          .filter(
            item =>
              item &&
              typeof item === 'object' &&
              'title' in item &&
              'description' in item &&
              item.title &&
              item.description &&
              !this.isPlaceholder(item.title as string) &&
              !this.isPlaceholder(item.description as string)
          )
          .map(item => ({
            id: (item.id as string) || '',
            title: (item.title as string) || '',
            description: (item.description as string) || '',
          }));

        if (validItems.length > 0) {
          return {
            title: 'Cultural & Social Aspects',
            items: validItems,
          };
        }
        return undefined;
      }

      // Handle object format (old API format): {title: string, items: string[]}
      if (
        typeof culturalAspectsData === 'object' &&
        'title' in culturalAspectsData &&
        'items' in culturalAspectsData &&
        Array.isArray(culturalAspectsData.items) &&
        culturalAspectsData.items.length > 0 &&
        !this.isPlaceholder((culturalAspectsData.title as string) || '')
      ) {
        return {
          title:
            (culturalAspectsData.title as string) ||
            'Cultural & Social Aspects',
          items: (culturalAspectsData.items as string[]).filter(
            item => item && !this.isPlaceholder(item)
          ),
        };
      }

      return undefined;
    })();

    // Transform historical significance (include image/imageUrls for section header)
    const historicalSignificance: HistoricalSignificance | undefined = (() => {
      if (!responseData.historicalSignificance) return undefined;
      const raw = responseData.historicalSignificance as {
        title?: string;
        items?: unknown[];
        image?: string;
        imageUrl?: string;
        imageUrls?: string[];
        images?: Array<{ url?: string }>;
      };

      // Handle array format (new API format): [{id, title, description, image?, ...}, ...]
      if (
        Array.isArray(responseData.historicalSignificance) &&
        responseData.historicalSignificance.length > 0
      ) {
        const validItems = responseData.historicalSignificance
          .filter(
            item =>
              item &&
              typeof item === 'object' &&
              'title' in item &&
              'description' in item &&
              item.title &&
              item.description &&
              !this.isPlaceholder(item.title as string) &&
              !this.isPlaceholder(item.description as string)
          )
          .map(item => ({
            id: (item.id as string) || '',
            title: (item.title as string) || '',
            description: (item.description as string) || '',
          }));

        if (validItems.length > 0) {
          const imageUrls = this.getImageUrls(
            responseData.historicalSignificance[0] as Record<string, unknown>
          );
          return {
            title: 'Historical Significance',
            items: validItems,
            ...(imageUrls.length > 0 && {
              image: imageUrls[0],
              imageUrls,
            }),
          };
        }
        return undefined;
      }

      // Handle object format (old API format): {title: string, items: string[], image?, imageUrls?, images?}
      if (
        typeof responseData.historicalSignificance === 'object' &&
        'title' in responseData.historicalSignificance &&
        'items' in responseData.historicalSignificance &&
        Array.isArray(responseData.historicalSignificance.items) &&
        responseData.historicalSignificance.items.length > 0 &&
        !this.isPlaceholder(
          (responseData.historicalSignificance.title as string) || ''
        )
      ) {
        const imageUrls = this.getImageUrls(raw);
        return {
          title:
            (responseData.historicalSignificance.title as string) ||
            'Historical Significance',
          items: (responseData.historicalSignificance.items as string[]).filter(
            item => item && !this.isPlaceholder(item)
          ),
          ...(imageUrls.length > 0 && {
            image: imageUrls[0],
            imageUrls,
          }),
        };
      }

      return undefined;
    })();

    const transformedResponse = {
      bestTimeToVisit,
      travelModes,
      travelModeDescription: responseData.travelModeDescription as
        | string
        | undefined,
      accessibility,
      terrainTypes,
      navigationDifficulty: (() => {
        const navDiff = responseData.navigationDifficulty;
        if (!navDiff) return undefined;

        // If it's already a string, return it
        if (typeof navDiff === 'string') {
          return navDiff.trim() !== '' ? navDiff : undefined;
        }

        // If it's an object with title and items
        if (
          typeof navDiff === 'object' &&
          navDiff !== null &&
          'items' in navDiff &&
          Array.isArray(navDiff.items) &&
          navDiff.items.length > 0
        ) {
          return {
            title: (navDiff.title as string) || 'Navigation Difficulty',
            items: navDiff.items.filter(
              (item: unknown) =>
                typeof item === 'string' &&
                item.trim() !== '' &&
                !this.isPlaceholder(item)
            ) as string[],
          };
        }

        return undefined;
      })(),
      locationHistory,
      historicalSignificance,
      culturalAspects,
      foodSpecialties,
      localLanguages: localLanguages.length > 0 ? localLanguages : undefined,
      localExperiences,
      handicraftsAndSouvenirs,
      activities,
      amenitiesAndFacilities,
      safetyAndSecurity,
    };

    console.log('✅ [TravelAssistantTransformer] transformQuickPeek result:', {
      hasBestTimeToVisit: !!transformedResponse.bestTimeToVisit,
      travelModesCount: transformedResponse.travelModes?.length || 0,
      hasAccessibility: !!transformedResponse.accessibility,
      terrainTypesCount: transformedResponse.terrainTypes?.length || 0,
      hasNavigationDifficulty: !!transformedResponse.navigationDifficulty,
      hasHistoricalSignificance: !!transformedResponse.historicalSignificance,
      hasCulturalAspects: !!transformedResponse.culturalAspects,
      foodSpecialtiesCount: transformedResponse.foodSpecialties?.length || 0,
      localLanguagesCount: transformedResponse.localLanguages?.length || 0,
      localExperiencesCount: transformedResponse.localExperiences?.length || 0,
      handicraftsCount:
        transformedResponse.handicraftsAndSouvenirs?.length || 0,
      hasActivities: !!transformedResponse.activities,
      hasAmenities: !!transformedResponse.amenitiesAndFacilities,
      hasSafety: !!transformedResponse.safetyAndSecurity,
      responseKeys: Object.keys(transformedResponse),
    });

    return transformedResponse;
  }

  /**
   * Build authentication headers
   */
  static buildAuthHeaders(
    accessToken?: string,
    refreshToken?: string
  ): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    if (accessToken && refreshToken) {
      headers['x-access-token'] = accessToken;
      headers['x-refresh-token'] = refreshToken;
    }

    return headers;
  }
}
